import './vehicle-add-edit.scss';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { observer } from 'mobx-react-lite';
import { IAddEditVehicleProps, IVehicleFormData } from './vehicle-add-edit.types';
import { toCreateVehicleModel, toUpdateVehicleModel } from './vehicle-add-edit.helpers';
import { Button, DatePicker, Divider, Form, Input, Select, SelectProps, Space, Switch } from 'antd';
import { useMedia } from 'common/hooks/use-media/use-media.hook';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { AddEditPageHeader } from 'features/eiq-connect/components/add-edit-page-header/add-edit-page-header';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import eiqConnectStore from 'features/eiq-connect/store/eiq-connect.store';
import vehicleService from 'common/services/vehicle-service/vehicle.service';
import residentsService from 'common/services/residents-service/residents.service';
import { AddEditActions } from 'features/eiq-connect/components/add-edit-actions/add-edit-actions';
import { DATE_SHORT_MMM_DD_YYYY } from 'common/constans/date.constants';
import { notification } from 'common/utils/notification';
import { IVehicleRead } from 'common/services/residents-service/residents.service.types';
import { StrToDate } from 'common/helpers/date.helper';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { formatAddressLabel } from 'common/helpers/address.helper';
import SelectState from 'common/components/form-elements/select-state/select-state';
import configurationStore from 'common/stores/configuration/configuration.store';
import VehicleDeleteModal from '../vehicle-delete-modal/vehicle-delete-modal';
import FileService from '../../../../../common/services/file-service/file.service';
import { addEditVehicleValidation } from 'common/validation/vehicle.validation';
import { IConfirmDeleteVehicleProps } from '../vehicle-delete-modal/vehicle-delete-modal.types';
import FormItem from 'antd/es/form/FormItem';
import { trackPromise } from 'react-promise-tracker';
import { UploadPhoto } from 'common/components/photo-upload/upload-photo';
import { useUploadPhoto } from 'common/hooks/use-upload-photo/use-upload-photo';

const defaultLicenseState = configurationStore.configuration?.default.defaultLicenseState;

const initData: IVehicleFormData = {
  year: null,
  make: null,
  model: null,
  licenseNumber: null,
  licenseState: defaultLicenseState ?? null,
  color: null,
  owner: null,
  registrationAddress: null,
  addressId: null,
  rentStartDate: null,
  rentEndDate: null,
  photoUrl: null,
};

export const VehicleAddEdit = observer(({ pageType }: IAddEditVehicleProps) => {
  const [form] = Form.useForm();
  const { isMobileOrTable } = useMedia();
  const { id } = useParams();
  const navigate = useNavigate();

  const [addressOptions, setAddressOptions] = useState<SelectProps['options']>([]);
  const [isRented, setIsRented] = useState(false);
  const [currentVehicle, setCurrentVehicle] = useState<IVehicleRead>();
  const isFormSubmittedSuccessful = useRef(false);

  useEffect(() => {
    return () => {
      if (!isFormSubmittedSuccessful.current) {
        notification.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (!eiqConnectStore.residentDetails) {
      return;
    }

    const addressOptions = eiqConnectStore.residentDetails?.addresses?.map((item) => ({
      value: item.address.id,
      label: formatAddressLabel(item.address),
    }));

    setAddressOptions(addressOptions || []);

    if (pageType === 'EDIT') {
      const vehicle = eiqConnectStore.residentDetails.addresses.flatMap((item) => item.vehicles).find((item) => item.id === Number(id!));
      setCurrentVehicle(vehicle);
    }
  }, [eiqConnectStore.residentDetails]);

  useEffect(() => {
    if (!form || !currentVehicle) {
      return;
    }

    const addressId = eiqConnectStore.residentDetails?.addresses?.find((item) => item.vehicles.includes(currentVehicle))?.address.id;

    const formData: IVehicleFormData = {
      year: currentVehicle.year,
      make: currentVehicle.make,
      model: currentVehicle.model,
      licenseNumber: currentVehicle.licenseNumber,
      licenseState: currentVehicle.licenseState,
      color: currentVehicle.color,
      owner: currentVehicle.owner,
      registrationAddress: currentVehicle.registrationAddress,
      addressId: addressId!,
      rentStartDate: StrToDate(currentVehicle.rentStartDate),
      rentEndDate: StrToDate(currentVehicle.rentEndDate),
      photoUrl: currentVehicle.photoUrl,
    };

    setIsRented(currentVehicle.rentStartDate !== null && currentVehicle.rentEndDate !== null);
    form.setFieldsValue(formData);
  }, [currentVehicle, form]);

  const updateResidentData = () => {
    residentsService
      .getResidentDetails(userAuthStore.webGuestResidentId!)
      .then((data) => {
        eiqConnectStore.setResidentDetails(data);
        navigate('/eiq-connect/vehicles');
      })
      .catch((e) => {
        notification.error({ message: 'An error occurred while getting resident details' });
      });
  };

  const currentAddressId = useMemo(() => {
    if (!currentVehicle) {
      return null;
    }

    return eiqConnectStore.residentDetails?.addresses?.find((item) => item.vehicles.includes(currentVehicle!))?.address.id;
  }, [currentVehicle]);

  const createVehicle = (residentId: number, values: IVehicleFormData) => {
    const addressId = addressOptions?.length === 1 ? addressOptions[0].value : values.addressId;
    trackPromise(
      vehicleService.createVehicle(residentId, Number(addressId), toCreateVehicleModel(values)).then(() => {
        isFormSubmittedSuccessful.current = true;
        updateResidentData();
        notification.success({ message: 'The new vehicle has been successfully added' });
      }),
    );
  };

  const updateVehicle = (residentId: number, values: IVehicleFormData) => {
    trackPromise(
      vehicleService.updateVehicle(residentId, currentAddressId!, toUpdateVehicleModel(values, currentVehicle!)).then(() => {
        isFormSubmittedSuccessful.current = true;
        updateResidentData();
        notification.success({ message: 'The vehicle has been successfully updated' });
      }),
    );
  };

  const handleOnFinish = async (values: IVehicleFormData) => {
    const residentId: number = userAuthStore.webGuestResidentId!;
    const file = await useUploadHock.getFile();

    if (file) {
      const photoUrl = await FileService.uploadPhoto(file);
      values.photoUrl = photoUrl.absoluteUri;
    } else {
      values.photoUrl = '';
    }

    if (pageType === 'ADD') {
      createVehicle(residentId, values);
    } else {
      updateVehicle(residentId, values);
    }
  };

  const handleRentedToggle = (checked: boolean) => {
    setIsRented(checked);
  };

  const deleteVehicleModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const residentId: number | null = userAuthStore.webGuestResidentId!;
      const data: IConfirmDeleteVehicleProps = {
        vehicle: currentVehicle!,
        residentId: residentId,
        addressId: currentAddressId!,
      };

      setModalInitData(data);
    },
    onClose: (isSuccess) => {
      if (isSuccess) {
        updateResidentData();
        notification.success({ message: 'The vehicle has been successfully deleted' });
      }
    },
  });

  const useUploadHock = useUploadPhoto({ currentUrl: currentVehicle?.photoUrl });

  const uploadText = (
    <p className="ant-upload-text">
      <span>{currentVehicle?.photoUrl ? 'Update' : 'Add'}</span> a copy of your vehicle registration
    </p>
  );

  return (
    <FormWrapper
      form={form}
      onFinish={handleOnFinish}
      initialValues={initData}
      layout="vertical"
      className="eiq-vehicle-add-edit eiq-add-edit-page">
      <AddEditPageHeader isMobileOrTablet={isMobileOrTable} pageType={pageType} />
      <div className="eiq-content">
        {pageType === 'ADD' && addressOptions && addressOptions.length > 1 && (
          <Space className="full-width">
            <div className="eiq-vehicle-block">
              <Form.Item name="addressId" label="Address" rules={addEditVehicleValidation.addressId}>
                <Select options={addressOptions} placeholder="Select address" />
              </Form.Item>
              <Divider className="account-diver" />
            </div>
          </Space>
        )}

        <Space className="full-width">
          <div className="eiq-vehicle-block">
            <Form.Item name="owner" label="Owner/Driver" rules={addEditVehicleValidation.owner}>
              <Input name="owner" placeholder="Enter owner/driver" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
        </Space>

        <Space className="full-width">
          <div className="eiq-vehicle-block">
            <Form.Item name="year" label="Year" rules={addEditVehicleValidation.year}>
              <Input name="year" placeholder="Enter year" type="number" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
          <div className="eiq-vehicle-block">
            <Form.Item name="make" label="Make" rules={addEditVehicleValidation.make}>
              <Input name="make" placeholder="Enter make" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
        </Space>

        <Space className="full-width">
          <div className="eiq-vehicle-block">
            <Form.Item name="model" label="Model" rules={addEditVehicleValidation.model}>
              <Input name="model" placeholder="Enter model" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
          <div className="eiq-vehicle-block">
            <Form.Item name="color" label="Color" rules={addEditVehicleValidation.color}>
              <Input name="color" placeholder="Enter color" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
        </Space>

        <Space className="full-width">
          <div className="eiq-vehicle-block">
            <Form.Item name="licenseNumber" label="License plate" rules={addEditVehicleValidation.licenseNumber}>
              <Input name="licenseNumber" placeholder="Enter license plate" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
          <div className="eiq-vehicle-block">
            <Form.Item name="licenseState" label="License Plate State" rules={addEditVehicleValidation.licenseState}>
              <SelectState placeholder="Select a state" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
        </Space>

        <Space className="full-width ant-space-with-uploader">
          <div className="eiq-vehicle-block">
            <Form.Item name="registrationAddress" label="Registration address">
              <Input name="registrationAddress" placeholder="Enter registration address" />
            </Form.Item>
            <Divider className="account-diver" />
          </div>
          <UploadPhoto hook={useUploadHock} uploadText={uploadText} />
        </Space>

        <Space className="full-width">
          <div className="eiq-vehicle-block">
            <FormItem label="Rented" valuePropName="checked">
              <Switch checked={isRented} onChange={handleRentedToggle} />
            </FormItem>
            <Divider className="account-diver" />
          </div>
        </Space>

        {isRented && (
          <Space className="full-width eiq-date-range">
            <Form.Item name="rentStartDate" label="From" rules={addEditVehicleValidation.rentStartDate}>
              <DatePicker placeholder="Start date" format={DATE_SHORT_MMM_DD_YYYY} />
            </Form.Item>
            <Form.Item name="rentEndDate" label="To" rules={addEditVehicleValidation.rentEndDate}>
              <DatePicker placeholder="End date" format={DATE_SHORT_MMM_DD_YYYY} />
            </Form.Item>
          </Space>
        )}

        {pageType === 'EDIT' && (
          <Button type="link" onClick={deleteVehicleModal.showModal}>
            Delete vehicle
          </Button>
        )}
      </div>

      {isMobileOrTable && (
        <div className="eiq-footer-actions">
          <AddEditActions type={pageType} />
        </div>
      )}

      <VehicleDeleteModal
        isEdit={false}
        initData={deleteVehicleModal?.initData}
        title="Delete"
        isOpen={deleteVehicleModal?.isOpenModal}
        onClose={deleteVehicleModal?.hideModal}
      />
    </FormWrapper>
  );
});
